import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getAnalytics } from 'firebase/analytics';
import {
  getFirestore,
  Firestore,
  DocumentData,
  CollectionReference,
  Query,
  DocumentReference,
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  setDoc,
  serverTimestamp,
  Timestamp,
  arrayUnion as firestoreArrayUnion,
  arrayRemove as firestoreArrayRemove,
  writeBatch,
  runTransaction as firestoreRunTransaction,
  onSnapshot
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { logger } from '../utils/logger';

// Validate required environment variables
const validateEnvVar = (name: string, value: string | undefined): string => {
  if (!value || value.trim() === '') {
    const errorMsg = `FATAL: Missing required environment variable: ${name}`;
    logger.error(errorMsg);
    throw new Error(errorMsg);
  }
  return value;
};

// Firebase Configuration - STRICT: No fallback values for security
const firebaseConfig = {
  apiKey: validateEnvVar('REACT_APP_FIREBASE_API_KEY', process.env.REACT_APP_FIREBASE_API_KEY),
  authDomain: validateEnvVar('REACT_APP_FIREBASE_AUTH_DOMAIN', process.env.REACT_APP_FIREBASE_AUTH_DOMAIN),
  projectId: validateEnvVar('REACT_APP_FIREBASE_PROJECT_ID', process.env.REACT_APP_FIREBASE_PROJECT_ID),
  storageBucket: validateEnvVar('REACT_APP_FIREBASE_STORAGE_BUCKET', process.env.REACT_APP_FIREBASE_STORAGE_BUCKET),
  messagingSenderId: validateEnvVar('REACT_APP_FIREBASE_MESSAGING_SENDER_ID', process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID),
  appId: validateEnvVar('REACT_APP_FIREBASE_APP_ID', process.env.REACT_APP_FIREBASE_APP_ID),
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || '' // Optional
};

// Safari compatibility check
const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

// Safari-specific Firebase initialization logging
if (isSafari || isIOS) {
  logger.firebase('Initializing Firebase for Safari/iOS', {
    projectId: firebaseConfig.projectId,
    authDomain: firebaseConfig.authDomain,
    userAgent: navigator.userAgent,
    connection: navigator.onLine ? 'online' : 'offline'
  });
} else {
  logger.firebase('Initializing Firebase', {
    projectId: firebaseConfig.projectId,
    authDomain: firebaseConfig.authDomain
  });
}

// Initialize the Firebase application with error handling
import { FirebaseApp } from 'firebase/app';
import { Analytics } from 'firebase/analytics';
import { Auth } from 'firebase/auth';

let app: FirebaseApp;
let analytics: Analytics | undefined;
let auth: Auth;
let db: Firestore;

try {
  logger.firebase('Attempting to initialize Firebase app...');
  app = initializeApp(firebaseConfig);
  logger.firebase('App initialized successfully', { appName: app.name });

  // Initialize Analytics with Safari-specific handling
  try {
    if (!isSafari && !isIOS) {
      // Only initialize analytics on non-Safari browsers
      analytics = getAnalytics(app);
      logger.firebase('Analytics initialized');
    } else {
      logger.firebase('Skipping analytics initialization on Safari/iOS for privacy');
    }
  } catch (analyticsError) {
    logger.warn('Analytics initialization failed (this is usually OK)', analyticsError);
  }

  // Initialize Firebase Authentication with retry logic
  try {
    logger.firebase('Initializing Authentication...');
    auth = getAuth(app);

    // Safari-specific auth persistence settings
    if (isSafari || isIOS) {
      // Use local persistence for better Safari compatibility
      import('firebase/auth').then(({ setPersistence, browserLocalPersistence }) => {
        setPersistence(auth, browserLocalPersistence).catch(persistenceError => {
          logger.warn('Failed to set auth persistence', persistenceError);
        });
      });
    }

    logger.firebase('Authentication initialized successfully');
  } catch (authError) {
    logger.error('Authentication initialization failed', authError);
    throw authError;
  }

  // Initialize Firestore with Safari-specific settings
  try {
    logger.firebase('Initializing Firestore...');
    db = getFirestore(app);

    // Safari-specific Firestore settings
    if (isSafari || isIOS) {
      // Enable offline persistence for Safari (but handle failures gracefully)
      import('firebase/firestore').then(({ enableIndexedDbPersistence }) => {
        enableIndexedDbPersistence(db).catch((persistenceError) => {
          if (persistenceError.code === 'failed-precondition') {
            logger.warn('Multiple tabs open, persistence can only be enabled in one tab at a time.');
          } else if (persistenceError.code === 'unimplemented') {
            logger.warn('The current browser does not support offline persistence');
          } else {
            logger.warn('Firestore persistence error', persistenceError);
          }
        });
      });
    }

    logger.firebase('Firestore initialized', { appName: app.name });
  } catch (firestoreError) {
    logger.error('Firestore initialization failed', firestoreError);
    throw firestoreError;
  }

} catch (initError) {
  logger.error('Critical Firebase initialization error', initError);
  
  // Create fallback objects to prevent app crashes
  app = {
    name: 'fallback-app',
    options: firebaseConfig,
    automaticDataCollectionEnabled: false
  } as any;
  
  auth = {
    currentUser: null,
    onAuthStateChanged: () => () => {},
    signInWithEmailAndPassword: () => Promise.reject(new Error('Firebase not available')),
    signOut: () => Promise.resolve()
  } as any;
  
  db = {
    collection: () => ({
      add: () => Promise.reject(new Error('Firebase not available')),
      doc: () => ({
        get: () => Promise.reject(new Error('Firebase not available')),
        set: () => Promise.reject(new Error('Firebase not available')),
        update: () => Promise.reject(new Error('Firebase not available')),
        delete: () => Promise.reject(new Error('Firebase not available'))
      })
    })
  } as any;
  
  // Re-throw the error to prevent app from starting with invalid configuration
  throw new Error(`Firebase initialization failed: ${initError instanceof Error ? initError.message : 'Unknown error'}`);
}

// Initialize Firebase Functions
const functions = getFunctions(app);

// Test Firebase connection and initialization
export const testFirebaseConnection = async (): Promise<{ success: boolean; message: string }> => {
  try {
    logger.firebase('Testing Firebase connection...');

    // Check if app is initialized
    if (!app) {
      logger.error('Firebase app not initialized');
      return { success: false, message: 'Firebase app not initialized' };
    }

    // Check if auth is initialized
    if (!auth) {
      logger.error('Firebase auth not initialized');
      return { success: false, message: 'Firebase auth not initialized' };
    }

    // Check if db is initialized
    if (!db) {
      logger.error('Firebase database not initialized');
      return { success: false, message: 'Firebase db not initialized' };
    }

    // Test authentication state
    const currentUser = auth.currentUser;
    if (currentUser) {
      logger.firebase('Current user authenticated', {
        uid: currentUser.uid,
        email: currentUser.email,
        isAnonymous: currentUser.isAnonymous
      });
    } else {
      logger.firebase('No user currently logged in');
    }

    // Try to access Firestore (this will test connectivity)
    try {
      const testCollection = collection(db, 'users');
      logger.firebase('Successfully accessed Firestore collection');
      return { success: true, message: 'Firebase connection successful' };
    } catch (dbError) {
      logger.error('Firestore connection test failed', dbError);
      return {
        success: false,
        message: `Firestore connection failed: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`
      };
    }
  } catch (error) {
    logger.error('Firebase connection test failed', error);
    return {
      success: false,
      message: `Firebase connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

// Export Firestore functions for v9 modular API
export {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  setDoc,
  serverTimestamp,
  writeBatch,
  firestoreRunTransaction as runTransaction,
  onSnapshot
};

// Export Firestore types for type checking
export type { 
  DocumentData,
  CollectionReference,
  Query,
  DocumentReference,
  Firestore 
};

// Export Firebase services for use in application
const storage = getStorage(app);

// Log Firebase initialization complete
logger.firebase('Firebase services initialized successfully', {
  auth: !!auth,
  firestore: !!db,
  storage: !!storage,
  functions: !!functions
});

export { 
  auth, 
  db, 
  storage, 
  functions,
  httpsCallable,
  Timestamp,
  firestoreArrayUnion as arrayUnion,
  firestoreArrayRemove as arrayRemove
};
export type { Timestamp as TimestampType };

export default app; 